import { useState, useEffect, ReactElement } from 'react';
import Button from '@material-ui/core/Button';
import { Stack } from '@mui/material';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';

import {
  getCounterDurationSec,
  resetCounterDuration,
} from '~/ui/components/reusable/ActivityCounter/helper';
import createFormInitialValues from './form/create/initialValues';
import getEditFormInitialValues from './form/edit/getInitialValues';
import Modal from '~/ui/components/common/Modal';
import Loader from '~/ui/components/common/Loader';
import NoteHeader from '~/ui/components/reusable/Notes/Header';
import NoteForm from '~/ui/components/reusable/Notes/NoteForm';
import ActivityCounter from '~/ui/components/reusable/ActivityCounter';
import LatestReadings from '~/ui/components/reusable/LatestReadings';
import NotesList from './NotesList';

import { IPatientInfo, IPatientLatestReadings } from '~/services/api/patients/types';
import { View } from './types';
import { IFormValues } from '~/ui/components/reusable/Notes/NoteForm/types';
import styles from './NotesModal.module.scss';
import { ActivityLogMode, NoteType } from '~/services/api/enums';
import { INote } from '~/services/api/notes/types';
import { IActivityLog } from '~/services/api/activityLogs/types';
import { IBloodPressureReading } from '~/services/api/readings/types';
import { isoToFormat } from '~/utils/date';
import { useIsActivityCounterEnabled } from '~/utils/useIsActivityCounterEnabled';

interface IProps {
  patientId: number;
  onClose: () => void;
}

const NotesModal = ({ patientId, onClose }: IProps): ReactElement => {
  const [isLoading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [view, setView] = useState(View.NotesList);
  const [patient, setPatient] = useState<IPatientInfo>({} as IPatientInfo);
  const [latestReadings, setLatestReadings] = useState<IPatientLatestReadings>(null);
  const [editNote, setEditNote] = useState<INote>(null);

  const { currentUser } = useStoreState(store => store.auth);

  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);

  const isActivityCounterEnabled = useIsActivityCounterEnabled(currentUser);
  const isNoteFormView = view === View.AddNote || view === View.EditNote;

  const handleAddNote = async (formValues: IFormValues) => {
    const { showReminder, reminderDate, ...restFormValues } = formValues;

    const payload = {
      ...restFormValues,
      reminderDate: showReminder ? isoToFormat(reminderDate, 'y-MM-dd') : null,
      typeId: NoteType.General,
    } as INote;

    try {
      setIsAdding(true);
      await api.notes.create(patientId, payload);
      setView(View.NotesList);
      showNotify('Note successfully created');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateNote = async (formValues: IFormValues) => {
    const { showReminder, reminderDate, ...restFormValues } = formValues;

    const payload = {
      ...editNote,
      ...restFormValues,
      reminderDate: showReminder ? isoToFormat(reminderDate, 'y-MM-dd') : null,
    } as INote;

    try {
      setIsUpdating(true);
      await api.notes.update(payload);
      setView(View.NotesList);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsUpdating(false);
    }
  };

  const sendActivityLog = async () => {
    const secondsSpent = getCounterDurationSec();

    const data = {
      mode: ActivityLogMode.Online,
      dateTime: new Date().toISOString(),
      secondsSpent,
      note: '',
    };

    try {
      await api.activityLogs.create(patientId, data as IActivityLog);
      resetCounterDuration();
      setView(View.NotesList);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const editNoteHandler = (note: INote) => {
    setEditNote(note);
    setView(View.EditNote);
  };

  const handleCloseEditNote = () => {
    setEditNote(null);
    setView(View.NotesList);
  };

  const handleClose = async () => {
    if (isActivityCounterEnabled) {
      await sendActivityLog();
    }
    onClose();
  };

  const onBloodPressureUpdated = (data: IBloodPressureReading) => {
    setLatestReadings(state => {
      if (!state) {
        return state;
      }

      return {
        ...state,
        latestReadings: {
          ...state.latestReadings,
          bloodPressure: data,
        },
      };
    });
  };

  const onMount = async () => {
    try {
      setLoading(true);

      const [patientData, latestReadingsData] = await Promise.all([
        api.patients.getInfo(patientId).then(res => res.data),
        api.patients.getLatestReadings(patientId).then(res => res.data),
      ]);

      setPatient(patientData);
      setLatestReadings(latestReadingsData);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <Modal
      open
      isDraggable
      title="Notes"
      maxWidth="md"
      className={styles.modal}
      onClose={handleClose}
    >
      <div className={styles.container}>
        {isLoading ? (
          <Loader />
        ) : (
          <Stack spacing={1}>
            <NoteHeader
              fullName={patient.fullName}
              subjectId={patient.subjectId}
              dateOfBirth={patient.dateOfBirth}
            />
            {isNoteFormView && latestReadings && (
              <LatestReadings
                latestReadings={latestReadings.latestReadings}
                notificationRules={latestReadings.notificationRules}
                timezone={patient.timezone}
                onBloodPressureUpdated={onBloodPressureUpdated}
              />
            )}
          </Stack>
        )}
        {view === View.NotesList && (
          <>
            <div className={styles.btnWrapper}>
              <Button variant="contained" color="primary" onClick={() => setView(View.AddNote)}>
                ADD NOTES
              </Button>
            </div>
            <NotesList patientId={patientId} onEdit={editNoteHandler} />
          </>
        )}
        {view === View.AddNote && (
          <NoteForm
            showBackBtn
            initialValues={createFormInitialValues}
            isProcessing={isAdding}
            onSubmit={handleAddNote}
            onClose={() => setView(View.NotesList)}
          />
        )}
        {view === View.EditNote && editNote && (
          <NoteForm
            mode="edit"
            showBackBtn
            initialValues={getEditFormInitialValues(editNote)}
            isProcessing={isUpdating}
            onSubmit={handleUpdateNote}
            onClose={handleCloseEditNote}
          />
        )}
        {isActivityCounterEnabled && (
          <div className={styles.footer}>
            <ActivityCounter patient={patient} className={styles.counterContainer} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default NotesModal;
